import React, { useEffect, useState, useRef } from 'react';
import { CalendarClock, Check, Laptop, X } from 'lucide-react';

import { mergeAnonymousAccount } from '../../utils/accountMerge';
import { auth } from '../../firebase/firebaseConfig';
import { isFirebaseAuthCancellation, sanitizeFirebaseAuthError } from '../../utils/errorSanitizer';
import { beginGooglePopupFlow } from '../../utils/googlePopupFocusReset';
import {
  trackGateShown,
  trackGateDismissed,
  trackSignUp,
  trackSignupPromptShown,
  trackSignupCompleted,
} from '../../utils/analytics';
import { ErrorMessage } from '../ErrorHandling';
import { ReactComponent as FlashCodeIcon } from '../../Icons/flashcode-icon_2.svg';
import './Auth.css';
import './GuestSaveModal.css';

const GuestSaveModal = ({ open, onSaved, onDismiss, dueLabel }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(null);
  const [error, setError] = useState(null);
  const googleFlowRef = useRef(null);

  useEffect(() => {
    if (open) return;
    setError(null);
    setSubmitting(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    trackGateShown({ surface: 'guest-save-modal' });
    trackSignupPromptShown({ surface: 'guest-save-modal' });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) onDismiss?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, submitting, onDismiss]);

  if (!open) return null;

  const handleGoogle = async () => {
    if (submitting) return;

    setSubmitting('google');
    setError(null);

    const { endGooglePopupFlow, isActiveFlow } = beginGooglePopupFlow(
      googleFlowRef,
      () => {
        if (auth.currentUser && !auth.currentUser.isAnonymous) return;
        setSubmitting((s) => (s === 'google' ? null : s));
      }
    );

    try {
      const result = await mergeAnonymousAccount({ provider: 'google' });
      endGooglePopupFlow();
      trackSignUp('google');
      trackSignupCompleted({ method: 'google', surface: 'guest-save-modal' });
      onSaved?.(result);
    } catch (err) {
      endGooglePopupFlow();
      if (!isActiveFlow()) return;
      if (isFirebaseAuthCancellation(err)) {
        setSubmitting(null);
        return;
      }
      console.error('[GuestSaveModal] google merge failed:', err);
      setError(sanitizeFirebaseAuthError(err));
      setSubmitting(null);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }
    setSubmitting('email');
    setError(null);
    try {
      const result = await mergeAnonymousAccount({
        provider: 'password',
        email,
        password,
      });
      trackSignUp('email');
      trackSignupCompleted({ method: 'email', surface: 'guest-save-modal' });
      onSaved?.(result);
    } catch (err) {
      if (isFirebaseAuthCancellation(err)) {
        setSubmitting(null);
        return;
      }
      console.error('[GuestSaveModal] email merge failed:', err);
      const code = err?.code;
      let friendly;
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        friendly = 'That email exists, but the password is wrong.';
      } else if (code === 'auth/weak-password') {
        friendly = 'Password is too weak. Try at least 8 characters.';
      } else {
        friendly = sanitizeFirebaseAuthError(err);
      }
      setError(friendly);
      setSubmitting(null);
    }
  };

  const handleDismiss = () => {
    if (submitting) return;
    trackGateDismissed('save-modal');
    onDismiss?.();
  };

  const handleBackdropMouseDown = (e) => {
    if (e.target !== e.currentTarget) return;
    handleDismiss();
  };

  const hasSession = Boolean(dueLabel);
  const headline =
    dueLabel === 'today'
      ? 'Your first review is later today.'
      : dueLabel === 'tomorrow'
        ? 'Your first review is tomorrow.'
        : dueLabel
          ? `Your first review lands ${dueLabel}.`
          : 'Save your progress with a free account.';
  const subtitle = hasSession
    ? 'Save your progress so it\u2019s waiting for you. Your snippet and review schedule come along.'
    : 'Create a free account to keep your cookbooks and review schedule.';
  const reviewDueSentence =
    dueLabel === 'today'
      ? 'later today'
      : dueLabel === 'tomorrow'
        ? 'tomorrow'
        : dueLabel || 'tomorrow';

  const isLoading = Boolean(submitting);

  return (
    <div className="guest-save-modal__backdrop" onMouseDown={handleBackdropMouseDown}>
      <div
        className="guest-save-modal__panel flashcode-auth-page__fade-up"
        role="dialog"
        aria-modal="true"
        aria-labelledby="guest-save-modal-title"
      >
        <div className="guest-save-modal__form-grid" aria-hidden />
        <div className="guest-save-modal__glow" aria-hidden />
        <button
          type="button"
          className="guest-save-modal__close"
          onClick={handleDismiss}
          disabled={isLoading}
          aria-label="Close"
        >
          <X size={16} strokeWidth={2} aria-hidden />
        </button>

        <div className="guest-save-modal__inner">
          <div className="guest-save-modal__topbar">
            <div className="flashcode-auth-page__logo guest-save-modal__logo">
              <span className="flashcode-auth-page__logo-mark" aria-hidden>
                <FlashCodeIcon />
              </span>
              <span className="flashcode-auth-page__logo-name">FlashCode</span>
            </div>
            <span
              className={`guest-save-modal__eyebrow${hasSession ? ' guest-save-modal__eyebrow--done' : ''}`}
            >
              {hasSession && <span className="guest-save-modal__eyebrow-dot" aria-hidden />}
              {hasSession ? 'Session complete' : 'Free account'}
            </span>
          </div>

          <h2 id="guest-save-modal-title" className="flashcode-auth-page__form-title">
            {headline}
          </h2>
          <p className="flashcode-auth-page__form-subtitle">{subtitle}</p>

          <ul className="guest-save-modal__recap" aria-label="What you keep">
            {hasSession ? (
              <>
                <li className="guest-save-modal__recap-item">
                  <span className="guest-save-modal__recap-icon guest-save-modal__recap-icon--done" aria-hidden>
                    <Check size={12} strokeWidth={2.5} />
                  </span>
                  <span className="guest-save-modal__recap-text">
                    Your first snippet, typed from memory
                  </span>
                  <span className="guest-save-modal__recap-badge guest-save-modal__recap-badge--done">
                    Done
                  </span>
                </li>
                <li className="guest-save-modal__recap-item">
                  <span className="guest-save-modal__recap-icon" aria-hidden>
                    <CalendarClock size={12} strokeWidth={2} />
                  </span>
                  <span className="guest-save-modal__recap-text">
                    First review {reviewDueSentence}
                  </span>
                  <span className="guest-save-modal__recap-badge">Scheduled</span>
                </li>
              </>
            ) : (
              <li className="guest-save-modal__recap-item">
                <span className="guest-save-modal__recap-icon" aria-hidden>
                  <CalendarClock size={12} strokeWidth={2} />
                </span>
                <span className="guest-save-modal__recap-text">
                  Cookbooks and review schedule stay put
                </span>
              </li>
            )}
            <li className="guest-save-modal__recap-item">
              <span className="guest-save-modal__recap-icon" aria-hidden>
                <Laptop size={12} strokeWidth={2} />
              </span>
              <span className="guest-save-modal__recap-text">
                Pick up on your computer or tablet
              </span>
            </li>
          </ul>

          <div className="flashcode-auth-page__messages">
            {error && <ErrorMessage message={error} type="error" />}
          </div>

          <button
            type="button"
            className={`flashcode-auth-page__btn-google${submitting === 'google' ? ' flashcode-auth-page__btn-google--loading' : ''}`}
            onClick={handleGoogle}
            disabled={isLoading}
          >
            <span className="flashcode-auth-page__btn-google-icon" aria-hidden>
              <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                <path
                  d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908C16.658 14.015 17.64 11.707 17.64 9.2z"
                  fill="#4285F4"
                />
                <path
                  d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
                  fill="#34A853"
                />
                <path
                  d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
                  fill="#FBBC05"
                />
                <path
                  d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
                  fill="#EA4335"
                />
              </svg>
            </span>
            <span className="flashcode-auth-page__btn-google-label">
              {submitting === 'google' ? 'Saving with Google' : 'Save with Google'}
            </span>
          </button>

          <div className="flashcode-auth-page__divider">
            <div className="flashcode-auth-page__divider-line" aria-hidden />
            <span className="flashcode-auth-page__divider-text">or use email</span>
            <div className="flashcode-auth-page__divider-line" aria-hidden />
          </div>

          <form onSubmit={handleEmailSubmit} noValidate>
            <div className="flashcode-auth-page__form-fields">
              <div>
                <label htmlFor="guest-save-modal-email" className="flashcode-auth-page__field-label">
                  Email
                </label>
                <input
                  id="guest-save-modal-email"
                  className="flashcode-auth-page__input"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>
              <div>
                <label htmlFor="guest-save-modal-password" className="flashcode-auth-page__field-label">
                  Password
                </label>
                <input
                  id="guest-save-modal-password"
                  className="flashcode-auth-page__input"
                  type="password"
                  autoComplete="new-password"
                  placeholder="Min. 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              className={`flashcode-auth-page__btn-primary${submitting === 'email' ? ' flashcode-auth-page__btn-primary--loading' : ''}`}
              disabled={isLoading}
            >
              {submitting === 'email' ? 'Saving your progress' : 'Save my progress →'}
            </button>
          </form>

          <p className="flashcode-auth-page__footer-toggle">
            <button
              type="button"
              className="flashcode-auth-page__footer-link"
              onClick={handleDismiss}
              disabled={isLoading}
            >
              Skip for now
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default GuestSaveModal;
